"use server";

import { revalidatePath } from "next/cache";
import { cookies } from "next/headers";
import { getSession } from "@/lib/auth/cookies";
import { canManageAi, canManageUsers } from "@/lib/auth/rbac";
import { withTenant } from "@/lib/auth/with-tenant";
import { prisma } from "@/lib/db";
import { isSpendRange } from "@/lib/ai/spend-series";
import { loadHomeIndexingSnapshot } from "@/lib/home/indexing-live";
import { getServerResources } from "@/lib/system/resources";
import { listMyChatSchedules } from "@/lib/chat/schedule";
import { HOME_QUOTA_RANGE_COOKIE } from "@/lib/ui/home-quota-range-cookie";
import { HOME_SPEND_RANGE_COOKIE } from "@/lib/ui/home-spend-range-cookie";
import {
  HOME_WIDGET_CONFIG_COOKIE,
  isConfigurableHomeWidget,
  clampHomeWidgetConfigEntry,
  parseHomeWidgetConfig,
  serializeHomeWidgetConfig,
  setHomeWidgetConfigEntry,
  clampHomeWidgetWidth,
  maxWidthForWidget,
} from "@/lib/ui/home-widget-config";
import { readDisplayTimeZone } from "@/lib/ui/timezone-cookie";
import {
  HOME_WIDGETS_COOKIE,
  filterHomeLayout,
  homeCanUseWidget,
  isHomeWidgetId,
  parseHomeLayout,
  placeHomeWidget,
  serializeHomeLayout,
  toggleHomeWidget,
  type HomeLayoutToken,
} from "@/lib/ui/home-widgets";

async function readWidgetConfigJar() {
  const jar = await cookies();
  return {
    jar,
    config: parseHomeWidgetConfig(jar.get(HOME_WIDGET_CONFIG_COOKIE)?.value),
  };
}

async function writeLayout(next: HomeLayoutToken[]) {
  const jar = await cookies();
  jar.set(HOME_WIDGETS_COOKIE, serializeHomeLayout(next), {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
}

export async function toggleHomeWidgetAction(id: string) {
  const session = await getSession();
  if (!session) {
    return;
  }

  if (!isHomeWidgetId(id) || !homeCanUseWidget(id, session.roles)) {
    return;
  }

  const jar = await cookies();
  const { config } = await readWidgetConfigJar();
  const next = toggleHomeWidget(
    parseHomeLayout(jar.get(HOME_WIDGETS_COOKIE)?.value, session.roles),
    id,
    config,
  );
  await writeLayout(next);
  revalidatePath("/");
}

export async function placeHomeWidgetAction(
  id: string,
  row: number,
  column: number,
) {
  const session = await getSession();
  if (!session) {
    return;
  }

  if (!isHomeWidgetId(id) || !homeCanUseWidget(id, session.roles)) {
    return;
  }
  if (!Number.isInteger(row) || row < 1) {
    return;
  }
  if (!Number.isInteger(column) || column < 1) {
    return;
  }

  const jar = await cookies();
  const { config } = await readWidgetConfigJar();
  const current = filterHomeLayout(
    parseHomeLayout(jar.get(HOME_WIDGETS_COOKIE)?.value, session.roles),
    (item) => homeCanUseWidget(item, session.roles),
  );
  await writeLayout(placeHomeWidget(current, id, row, column, config));
  revalidatePath("/");
}

export async function setHomeWidgetConfigAction(formData: FormData) {
  const session = await getSession();
  if (!session) {
    return;
  }

  const id = String(formData.get("widgetId") ?? "");
  if (!isConfigurableHomeWidget(id) || !homeCanUseWidget(id, session.roles)) {
    return;
  }

  const units = clampHomeWidgetWidth(
    Number(formData.get("units")),
    maxWidthForWidget(id),
  );
  const columns = formData
    .getAll("columns")
    .map((value) => String(value).trim())
    .filter((value) => value.length > 0);
  const entry = clampHomeWidgetConfigEntry(id, { units, columns });

  const jar = await cookies();
  const config = setHomeWidgetConfigEntry(
    parseHomeWidgetConfig(jar.get(HOME_WIDGET_CONFIG_COOKIE)?.value),
    id,
    entry,
  );
  jar.set(HOME_WIDGET_CONFIG_COOKIE, serializeHomeWidgetConfig(config), {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
  revalidatePath("/");
}

export async function setHomeSpendRangeAction(formData: FormData) {
  const session = await getSession();
  if (!session || !canManageAi(session.roles)) {
    return;
  }

  const range = String(formData.get("range") ?? "");
  if (!isSpendRange(range)) {
    return;
  }

  const jar = await cookies();
  jar.set(HOME_SPEND_RANGE_COOKIE, range, {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
  revalidatePath("/");
}

export async function setHomeQuotaRangeAction(formData: FormData) {
  const session = await getSession();
  if (!session) {
    return;
  }

  const range = String(formData.get("range") ?? "");
  if (!isSpendRange(range)) {
    return;
  }

  const jar = await cookies();
  jar.set(HOME_QUOTA_RANGE_COOKIE, range, {
    httpOnly: true,
    sameSite: "lax",
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
  });
  revalidatePath("/");
}

export async function listHomeIndexingAction() {
  return loadHomeIndexingSnapshot();
}

export async function listHomeServerAction() {
  const session = await getSession();
  if (!session || !canManageUsers(session.roles)) {
    return null;
  }
  return getServerResources();
}

export async function listHomeSchedulesAction() {
  const session = await getSession();
  if (!session) {
    return [];
  }
  return withTenant(async () => {
    const tenant = await prisma.tenant.findUnique({
      where: { id: session.tenantId },
      select: { timeZone: true },
    });
    const timeZone = await readDisplayTimeZone(tenant?.timeZone);
    return listMyChatSchedules({
      userId: session.userId,
      displayTimeZone: timeZone,
    });
  });
}
